/**
 * SourceRibbon — richer successor to SourcesPanel.
 *
 * Renders each retrieved chunk as a SourceCard, with:
 * - Filter chips (All / Library / Web / Docs / M365)
 * - Live counter ("12 sources across 3 libraries")
 * - Pin toggle per card (client-side only, persisted in state)
 * - Cross-highlight: listens for `deep:citation-hover` CustomEvents and
 *   pulses the matching card. The emitting side lives in
 *   `citation-link.tsx`.
 */
import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Document } from "@/api/models"
import type { DeepActivityEvent, DeepSource } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { citationMatchesSource, type SourceCardData } from "./source-card-utils"
import { collectSourceCards } from "./source-ribbon-utils"
import { SourceCard } from "./SourceCard"

type FilterKey = "all" | "library" | "web" | "doc" | "m365" | "other"

const FILTER_ORDER: { key: FilterKey; labelKey: string }[] = [
  { key: "all", labelKey: "deepExplorer.source_filters.all" },
  { key: "library", labelKey: "deepExplorer.source_filters.library" },
  { key: "web", labelKey: "deepExplorer.source_filters.web" },
  { key: "doc", labelKey: "deepExplorer.source_filters.doc" },
  { key: "m365", labelKey: "deepExplorer.source_filters.m365" },
]

/** Build a compact summary string: "12 sources across 3 libraries". */
function summariseSources(sources: SourceCardData[], t: TFunction): string {
  if (sources.length === 0) return t("deepExplorer.source_summary_empty")
  const libraries = new Set(
    sources.filter((s) => s.type === "library" && s.library).map((s) => s.library)
  )
  if (libraries.size > 0) {
    return t("deepExplorer.source_summary_libraries", {
      count: sources.length,
      libraries: libraries.size,
    })
  }
  return t("deepExplorer.source_summary_count", { count: sources.length })
}

interface SourceRibbonProps {
  activityLog: DeepActivityEvent[]
  /** Legacy fallback — older events only populated `sources` not `activityLog`. */
  legacySources?: DeepSource[]
  /** Optional verification summary from the fact-checker subagent. */
  verificationSummary?: {
    total: number
    verified: number
    unverified: number
  } | null
  /**
   * Citation section (search-service id / session type) — forwarded to each
   * SourceCard so `openPreview` resolves the correct indexed-file route.
   */
  citationSection?: string
  /**
   * Library documents attached to the current chat response. Forwarded to
   * SourceCards so `openPreview` can fall back to the unmodified original
   * when the library is configured for that.
   */
  documents?: [string, Document][]
  /**
   * Activity cards merged with saved message documents. When this list is
   * non-empty it replaces the activity-only collection, so a library row
   * whose preview was dropped still renders.
   */
  sourceCards?: SourceCardData[]
  /**
   * Override the "click to preview" behaviour on each card. Used by the
   * Deep Explorer panel to render previews in its own inline Viewer hero
   * instead of replacing the panel with the content sidebar.
   */
  onOpenPreview?: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
}

export function SourceRibbon({
  activityLog,
  legacySources = [],
  verificationSummary = null,
  citationSection = "",
  documents,
  sourceCards,
  onOpenPreview,
}: SourceRibbonProps) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<FilterKey>("all")
  const [pinned, setPinned] = useState<Set<string>>(new Set())
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  const cards = useMemo(() => {
    if (sourceCards && sourceCards.length > 0) return sourceCards
    const primary = collectSourceCards(activityLog)
    if (primary.length > 0) return primary
    // Legacy fallback: synthesise cards from DeepSource.
    return legacySources.map<SourceCardData>((s, i) => ({
      id: `legacy:${i}:${s.title}`,
      title: s.title,
      snippet: s.snippet,
      type: s.type,
      tool: s.tool,
      timestamp: s.timestamp,
      matchKeys: [s.title],
      citationCount: 1,
    }))
  }, [activityLog, legacySources, sourceCards])

  const filtered = useMemo(
    () => (filter === "all" ? cards : cards.filter((c) => c.type === filter)),
    [cards, filter]
  )

  const togglePin = useCallback((id: string) => {
    setPinned((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Listen for citation-hover CustomEvents dispatched by the deliverable.
  useEffect(() => {
    function onHover(ev: Event) {
      const detail = (ev as CustomEvent<{ citation: string } | undefined>).detail
      const citation = detail?.citation ?? ""
      if (!citation) {
        setHighlightedId(null)
        return
      }
      const hit = cards.find((c) => citationMatchesSource(citation, c.matchKeys))
      setHighlightedId(hit?.id ?? null)
    }
    window.addEventListener("deep:citation-hover", onHover as EventListener)
    return () => window.removeEventListener("deep:citation-hover", onHover as EventListener)
  }, [cards])

  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      all: cards.length,
      library: 0,
      web: 0,
      doc: 0,
      m365: 0,
      other: 0,
    }
    for (const c of cards) counts[c.type]++
    return counts
  }, [cards])

  if (cards.length === 0) {
    return (
      <div
        aria-label={t("deepExplorer.source_ribbon_aria")}
        className="text-muted-foreground flex flex-col items-center gap-2 py-6 text-center text-xs"
      >
        <p>{t("deepExplorer.no_source_cards_title")}</p>
        <p className="text-[11px]">{t("deepExplorer.no_source_cards_hint")}</p>
      </div>
    )
  }

  const verificationPct =
    verificationSummary && verificationSummary.total > 0
      ? Math.round((verificationSummary.verified / verificationSummary.total) * 100)
      : null

  return (
    <section aria-label={t("deepExplorer.source_ribbon_aria")} className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-muted-foreground text-[11px] font-medium">
          {summariseSources(cards, t)}
        </span>
        {pinned.size > 0 && (
          <span className="text-primary text-[11px]">
            {t("deepExplorer.pinned_count", { count: pinned.size })}
          </span>
        )}
      </div>

      {verificationSummary && verificationSummary.total > 0 && (
        <div
          aria-live="polite"
          role="status"
          className={cn(
            "rounded-md border px-2 py-1 text-[11px]",
            verificationSummary.unverified === 0
              ? STATUS_TONES.success.soft
              : "border-warning-ink/40 bg-warning/30 text-warning-ink"
          )}
        >
          {verificationSummary.unverified === 0 ? (
            <span>
              {t("deepExplorer.verified_all", {
                verified: verificationSummary.verified,
                total: verificationSummary.total,
                pct: verificationPct,
              })}
            </span>
          ) : (
            <span>
              {t("deepExplorer.verified_with_issues", {
                verified: verificationSummary.verified,
                total: verificationSummary.total,
                unverified: verificationSummary.unverified,
              })}
            </span>
          )}
        </div>
      )}

      <div
        aria-label={t("deepExplorer.source_filter_aria")}
        className="flex flex-wrap gap-1 px-1"
        role="tablist"
      >
        {FILTER_ORDER.map(({ key, labelKey }) => {
          const count = filterCounts[key]
          const active = filter === key
          // Hide filter chips for categories with zero entries (except "all").
          if (count === 0 && key !== "all") return null
          return (
            <button
              key={key}
              aria-selected={active}
              role="tab"
              type="button"
              className={cn(
                "focus-visible:ring-ring rounded-full border px-2 py-0.5 text-[11px] transition-colors focus-visible:ring-2 focus-visible:outline-none",
                active
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setFilter(key)}
            >
              {t(labelKey)} <span className="text-[10px] opacity-70">({count})</span>
            </button>
          )
        })}
      </div>

      <ul className="flex flex-col gap-1.5 overflow-y-auto px-1">
        {filtered.map((source) => (
          <SourceCard
            key={source.id}
            citationSection={citationSection}
            documents={documents}
            highlighted={highlightedId === source.id || pinned.has(source.id)}
            pinned={pinned.has(source.id)}
            source={source}
            onOpenPreview={onOpenPreview}
            onTogglePin={togglePin}
          />
        ))}
        {filtered.length === 0 && (
          <li className="text-muted-foreground py-3 text-center text-[11px]">
            {t("deepExplorer.no_sources_in_category")}
          </li>
        )}
      </ul>
    </section>
  )
}
